import { getUrl } from '../../config';
import React, { useState } from "react";
import styled from "@emotion/styled";
import * as Yup from "yup";
import { Formik } from "formik";
import axios from "axios";

import {
  Alert as MuiAlert,
  Button,
  TextField as MuiTextField,
} from "@mui/material";
import { spacing } from "@mui/system";

import useAuth from "../../hooks/useAuth";

const Alert = styled(MuiAlert)(spacing);

const TextField = styled(MuiTextField)(spacing);

const darkInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
    color: 'rgba(255, 255, 255, 0.95)',
    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.35)' },
    '&.Mui-focused fieldset': { borderColor: '#4782da' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
  '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.6)' },
};

function SignIn({ dark = false }) {
  const { signIn } = useAuth();
  const [showTwoFactor, setShowTwoFactor] = useState(false);

  return (
    <Formik
      initialValues={{
        username: "",
        password: "",
        twoFactorCode: "",
        submit: false,
      }}
      validationSchema={Yup.object().shape({
        username: Yup.string()
          .max(255)
          .required("Utilisateur est requis"),
        password: Yup.string().max(255).required("Mot de passe est requis"),
        twoFactorCode: Yup.string()
          .test('two-factor-required', 'Code 2FA requis', function (value) {
            if (this.parent.showTwoFactor) {
              return !!value;
            }
            return true;
          })
      })}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {

        try {
          const response = await axios.post(getUrl() + "/api/auth/login", {
            username: values.username,
            password: values.password,
            twoFactorCode: values.twoFactorCode
          });

          if (response.data.twoFactorRequired) {
            setShowTwoFactor(true);
            setStatus({ success: false });
            setErrors({ submit: "Code 2FA requis" });
            return;
          }

          await signIn(values.username, values.password, values.twoFactorCode);
        } catch (error) {
          if (error.response?.data?.twoFactorRequired) {
            setShowTwoFactor(true);
            setStatus({ success: false });
            setErrors({ submit: "Code 2FA requis" });
          } else {
            setStatus({ success: false });
            setErrors({ submit: "Identifiant Incorrect" });
          }
          setSubmitting(false);
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
      }) => (
        <form noValidate onSubmit={handleSubmit}>
          {errors.submit && (
            <Alert
              mt={0}
              mb={3}
              severity="warning"
              sx={{
                borderRadius: '8px',
                ...(dark && { backgroundColor: 'rgba(255, 152, 0, 0.15)', color: '#ffb74d' }),
              }}
            >
              {errors.submit}
            </Alert>
          )}
          <TextField
            type="text"
            name="username"
            label="Utilisateur"
            value={values.username}
            error={Boolean(touched.username && errors.username)}
            fullWidth
            helperText={touched.username && errors.username}
            onBlur={handleBlur}
            onChange={handleChange}
            my={2.5}
            variant="outlined"
            sx={dark ? darkInputSx : { '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: 'background.paper' } }}
          />
          <TextField
            type="password"
            name="password"
            label="Mot de passe"
            value={values.password}
            error={Boolean(touched.password && errors.password)}
            fullWidth
            helperText={touched.password && errors.password}
            onBlur={handleBlur}
            onChange={handleChange}
            my={2.5}
            variant="outlined"
            sx={dark ? darkInputSx : { '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: 'background.paper' } }}
          />
          {showTwoFactor && (
            <TextField
              type="text"
              name="twoFactorCode"
              label="Code 2FA"
              value={values.twoFactorCode}
              error={Boolean(touched.twoFactorCode && errors.twoFactorCode)}
              fullWidth
              helperText={touched.twoFactorCode && errors.twoFactorCode}
              onBlur={handleBlur}
              onChange={handleChange}
              my={2.5}
              placeholder="Entrez le code à 6 chiffres"
              variant="outlined"
              sx={dark ? darkInputSx : { '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: 'background.paper' } }}
            />
          )}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            sx={{
              mt: 3,
              mb: 2,
              py: 1.5,
              borderRadius: '8px',
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            Connexion
          </Button>
        </form>
      )}
    </Formik >
  );
};

export default SignIn;
